import AddButton from "@/components/buttons/AddButton";
import { Tooltip } from "@/components/ui/tooltip";
import IdAndNameData from "@/data/IdAndNameData";
import { DataList, Flex, IconButton, Text, useDisclosure } from "@chakra-ui/react";
import { LuPencilLine, LuTrash } from "react-icons/lu";
import AbsenceBalanceDialog from "./AbsenceBalanceDialog";
import ConfirmDialog from "@/components/ConfirmDialog";
import { AbsenceBalance } from "@/api/AbsenceBalance";
import { absenceService } from "@/service/AbsenceService";
import { useEffect, useState } from "react";
import WaitingSpinner from "@/components/WaitingSpinner";
import { formatOverview } from "@/data/DateTimeUtil";
import { User } from "@/api/User";


type AbsenceBalanceSectionProps = {
    user: User;
    canEdit?: boolean;
};

const AbsenceBalanceSection = ({ user, canEdit = true }: AbsenceBalanceSectionProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const { open: isAbsenceBalanceOpen, onOpen: onAbsenceBalanceOpen, onClose: onAbsenceBalanceClose } = useDisclosure();
    const [absenceBalanceEntries, setAbsenceBalanceEntries] = useState<AbsenceBalance[]>([]);
    const [editAbsenceBalanceData, setEditAbsenceBalanceData] = useState<AbsenceBalance>(null);
    const { open: isDeleteAbsenceBalanceOpen, onOpen: onDeleteAbsenceBalanceOpen, onClose: onDeleteAbsenceBalanceClose } = useDisclosure();
    const [deleteData, setDeleteData] = useState<IdAndNameData<number> | null>(null);

    useEffect(() => {
        setIsLoading(true);
        if (user) {
            const p = absenceService.findAllBalance(user?.id).then(r => setAbsenceBalanceEntries(r));
            p.finally(() => setIsLoading(false));
        }
    }, [user]);

    const newAbsenceBalance = () => {
        setEditAbsenceBalanceData(null);
        onAbsenceBalanceOpen();
    }

    const saveAbsenceBalance = (ab: AbsenceBalance) => {
        console.log(ab);
        editAbsenceBalanceData ? absenceService.updateBalance(ab).then(id => {
            const idx = absenceBalanceEntries.findIndex(e => e.id === id);
            if (idx >= 0) {
                absenceBalanceEntries[idx] = ab;
                setAbsenceBalanceEntries([...absenceBalanceEntries]);
            }
        }) :
            absenceService.addBalance(ab).then(id => {
                ab.id = id;
                setAbsenceBalanceEntries(
                    [...absenceBalanceEntries, ab].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                );
            });
        setEditAbsenceBalanceData(null);
        onAbsenceBalanceClose();
    }

    const deleteAbsenceBalance = (id: number) => {
        absenceService.removeBalance(user?.id, id).then(() => {
            const idx = absenceBalanceEntries.findIndex(e => e.id === id);
            if (idx >= 0) {
                absenceBalanceEntries.splice(idx, 1);
                setAbsenceBalanceEntries([...absenceBalanceEntries]);
            }
        });
        setDeleteData(null);
        onDeleteAbsenceBalanceClose();
    }


    return (
        <>
            {canEdit && <AddButton caption='Neue Anpassung' onClick={newAbsenceBalance} />}
            {isLoading && <WaitingSpinner size='sm' />}
            {absenceBalanceEntries.length === 0 ?
                <Text textStyle='sm'>keine Einträge</Text> :
                <DataList.Root orientation={{ base: 'vertical', sm: 'horizontal' }} gap={{ base: '4', sm: '0' }}>
                    {absenceBalanceEntries.map((entry: AbsenceBalance) => {
                        const from = formatOverview(entry.createdAt);
                        return (
                            <DataList.Item key={entry.id} flexDirection={{ sm: 'row' }}>
                                <DataList.ItemLabel>{from}</DataList.ItemLabel>
                                <DataList.ItemValue>{entry.days}&nbsp;<small>Tage</small>&nbsp;{entry.comments ?? ''}</DataList.ItemValue>
                                {canEdit && (
                                    <Flex>
                                        <Tooltip content='Bearbeiten'>
                                            <IconButton aria-label="Bearbeiten" colorPalette="blue" variant="ghost" size="xs" onClick={() => {
                                                setEditAbsenceBalanceData(entry);
                                                onAbsenceBalanceOpen();
                                            }}>
                                                <LuPencilLine />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip content='Löschen'>
                                            <IconButton aria-label="Löschen" colorPalette="red" variant="ghost" size="xs" onClick={() => {
                                                let da: IdAndNameData<number> = {
                                                    id: entry.id,
                                                    name: `${entry.days} Tage ab ${from}`
                                                };
                                                setDeleteData(da);
                                                onDeleteAbsenceBalanceOpen();
                                            }}>
                                                <LuTrash />
                                            </IconButton>
                                        </Tooltip>
                                    </Flex>
                                )}
                            </DataList.Item>
                        );
                    })}
                </DataList.Root>}
            {canEdit && (
                <>
                    <AbsenceBalanceDialog isOpen={isAbsenceBalanceOpen} onClose={onAbsenceBalanceClose} user={user} entry={editAbsenceBalanceData} onSave={saveAbsenceBalance} />
                    <ConfirmDialog
                        heading={`${deleteData?.name} Löschen`}
                        isOpen={isDeleteAbsenceBalanceOpen}
                        onClose={onDeleteAbsenceBalanceClose}
                        onConfirm={async (): Promise<void> => deleteAbsenceBalance(deleteData.id)}
                    />
                </>
            )}
        </>
    )
}

export default AbsenceBalanceSection;
